/**
 * ChartTemplate, it's the template of all charts
 * we can build all kinds of customized charts based on it
 * we just need to pass different configurations to the template
 * and then we will get an instance of the chart
 *
 * Essentially, it is the customized wrapper of the chart class
 * it will init a chart instance within a canvas area
 *
 */
import { Chart } from 'chart.js';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { fadeInAnimation } from '@/app/styles';
import { showZeroPlugin } from '@/chart/plugins/show-zero-plugin';
import styled from 'styled-components';

// Enabled by default - any bar chart with value zero will be visible
Chart.pluginService.register(showZeroPlugin);

const ChartCanvas = styled.canvas.attrs({
  style: { width: '100%', height: '100%' },
})``;

// https://www.chartjs.org/docs/latest/general/responsive.html
const CanvasWrapper = styled.div`
  position: relative;
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
  ${(props) => (props?.fadeInAnimation ? fadeInAnimation(2) : null)}
`;

const initChart = (ctx, props) => {
  const { type, data, options, plugins } = props;
  const configs = { type, data, options, plugins };
  const chart = new Chart(ctx, configs);
  window.template = Chart;
  return chart;
};

/**
 * @param {object} props
 * @param {object} props.data - the dataset of the chart
 * @param {object} props.options - the options of the chart
 * @param {Array<any>} props.plugins - the plugins of the chart
 */
export function ChartTemplate(props) {
  const { type, data, options, plugins } = props;

  const chartRef = useRef(null);

  useEffect(() => {
    const ctx = chartRef.current; // ctx is the actual canvas node
    initChart(ctx, { type, data, options, plugins });
  }, [data, type, options, plugins]);

  return (
    <CanvasWrapper fadeInAnimation={props.fadeInAnimation}>
      <ChartCanvas ref={chartRef} />
    </CanvasWrapper>
  );
}

ChartTemplate.propTypes = {
  // type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  plugins: PropTypes.array, // not required, so that we can install plugins at a per-chart basis
};
